/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { type ReactElement } from 'react';
import { isRouteErrorResponse } from 'react-router';
import { useTranslation } from 'react-i18next';
import { SeoMeta } from '@/components/seo-meta';
import { ErrorBody } from './error-body';

/**
 * Copy + SEO wrapper around {@link ErrorBody} for a route error (404 / 403 / 500 / generic): it
 * resolves the strings for the status and renders **no chrome** (no header/footer), so the caller
 * controls the surrounding layout and width. The `_app` ErrorBoundary wraps it in the storefront
 * shell + `section-container` (UCR-2787); root's ErrorBoundary supplies its own minimal wrapper for
 * errors thrown outside the app layout and shares the same `ErrorBody` (UCR-3048).
 *
 * It also owns the document title for error pages (UCR-2985) — the `_app.$` catch-all never paints
 * (its loader either throws or the matched auth handler redirects), so without this a 404 tab would
 * show the raw URL instead of `Page not found | {siteName}`. Error pages are `noindex`.
 *
 * Requires an `I18nextProvider` (it reads the `routeError` namespace) — guaranteed in the `_app`
 * subtree. The CTA is a plain `<a href="/">` (full reload) so navigating home also clears the
 * error state.
 */
export function RouteErrorContent({ error }: { error: unknown }): ReactElement {
    const { t } = useTranslation('routeError');
    const status = isRouteErrorResponse(error) ? error.status : undefined;

    let title: string;
    let message: string | undefined;
    let secondaryMessage: string | undefined;
    let statusDetails: string | undefined;

    if (status === 404) {
        title = t('404.title');
        message = t('404.message');
        secondaryMessage = t('404.secondaryMessage');
        statusDetails = t('404.details');
    } else if (status === 403) {
        title = t('403.title');
        message = t('403.message');
        secondaryMessage = t('403.secondaryMessage');
    } else if (status === 500) {
        title = t('500.title');
        message = t('500.message');
        secondaryMessage = t('500.secondaryMessage');
    } else {
        title = t('defaultTitle');
    }

    return (
        <>
            <SeoMeta title={title} noIndex />
            <ErrorBody
                status={status}
                title={title}
                message={message}
                secondaryMessage={secondaryMessage}
                statusDetails={statusDetails}
                ctaHref="/"
                ctaLabel={t('goToHomepage')}
            />
        </>
    );
}
